import { TextField } from '@mui/material';
import React, { ChangeEvent, KeyboardEvent, useEffect, useRef } from 'react';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

export interface Props {
  code: string[];
  setCode: React.Dispatch<React.SetStateAction<string[]>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  triggerWhenCodeIsFull: () => void;
}

const SixCodeTextField: React.FC<React.PropsWithChildren<Props>> = ({
  code,
  setCode,
  setError,
  triggerWhenCodeIsFull
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  if (inputRefs.current.length === 0) {
    inputRefs.current = Array(6).fill(null);
  }

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // check if new input value is number
    if (value === '' || /^\d$/.test(value)) {
      // update char in state
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // if new char is number focus next input
      if (value !== '' && index < 5) {
        inputRefs.current[index + 1]?.focus();
        if (inputRefs.current[index + 1]?.value.trim() !== '') inputRefs.current[index + 1]?.select();
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
    // if backspace and input is empty focus previous input
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const getFullCode = () => {
    return code.join('');
  };

  useEffect(() => {
    const verificationCode = getFullCode();
    if (verificationCode.length === 6) triggerWhenCodeIsFull();
    else if (/^\d+$/.test(verificationCode) && verificationCode.length === 6) setError(true);
    else setError(false);
  }, [code]);

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedDataOnlyNumbers = pastedData.replace(/\D/g, '');

    // check if pasted is only numbers
    if (/^\d+$/.test(pastedDataOnlyNumbers)) {
      const digits = pastedDataOnlyNumbers.split('').slice(0, 6);
      const newCode = [...code];

      digits.forEach((digit, idx) => {
        if (idx < 6) {
          newCode[idx] = digit;
        }
      });

      setCode(newCode);

      const nextEmptyIndex = newCode.findIndex((digit) => digit === '');
      if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[5]?.focus();
      }
    }
  };

  return (
    <ComponentWrapper flexDirection="row" gap="18px">
      {code.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          variant="outlined"
          size="medium"
          value={digit}
          placeholder="#"
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e)}
          onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            style: {
              textAlign: 'center',
              width: '64px',
              height: '64px',
              fontSize: '1.5rem',
              padding: '0'
            }
          }}
        />
      ))}
    </ComponentWrapper>
  );
};

export default SixCodeTextField;
